import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { getJuryCampaigns, getCampaignParticipants, getCampaignRanking, getParticipantFiche,
         getJuryComments, saveJuryStartupComment, saveJuryGeneralComment, getTrophyStats,
         getParticipantDocuments, getParticipantDocumentPreview,
         getParticipantDocumentPageUrl,
         getJuryFavorite, setJuryFavorite, clearJuryFavorite,
         getJuryPortfolio, saveJuryPortfolio, getJuryVoteResults } from '../../api';
import { useToast } from '../../components/Toast';
import { ScoreGauge, RiskBadge } from '../../components/ScoreGauge';
import RadarChart from '../../components/RadarChart';
import DocumentViewer from '../../components/DocumentViewer';
import JuryResultsBoard from '../../components/JuryResultsBoard';
import { getStoredUser } from '../../api';
import { LogoImg, getDomain } from '../../components/StartupCard';
import { useLocation, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const CL_LABELS = ['BP', 'Ratio', 'Solidité Fi.', 'Maturité Fi.', 'Marché', 'Innovation', 'Équipe', 'Org. Comm.', 'Notoriété', 'RSE', 'RGPD', 'Conformité'];
// Libelles complets pour le tableau des notes ; CL_LABELS reste abrege car le
// radar ne dispose que de la place d'un axe.
// Meme convention que la jauge ISI et le classement : vert > 7, orange 4-7,
// rouge < 4. La couleur porte l'information autant que le chiffre.
const scoreColor = (v) => (v > 7 ? '#27AE60' : v >= 4 ? '#F39C12' : '#E74C3C');

// Le jury a renoncé au coup de cœur : la fonctionnalité est désactivée, non
// supprimée. Le drapeau coupe l'affichage et l'appel au serveur ; `favorite`
// reste donc nul, ce qui éteint du même coup tous les marqueurs ❤️ des listes
// et du classement. Repasser à true la rétablit — le serveur a son propre
// drapeau, COUP_DE_COEUR_ACTIF dans routes/jury.py, à remettre en même temps.
const COUP_DE_COEUR = false;

// Marqueur de sélection, relu à chaque affichage depuis `jury_visible` : une
// startup peut entrer ou sortir de la sélection de l'organisateur à tout moment.
function SelectionBadge({ selected, size = 10 }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px',
      borderRadius: 999, fontSize: size, fontWeight: 700, whiteSpace: 'nowrap',
      background: selected ? 'rgba(39,174,96,0.14)' : 'rgba(120,130,150,0.14)',
      color: selected ? '#1E8449' : '#5A6275',
      border: `1px solid ${selected ? 'rgba(39,174,96,0.35)' : 'rgba(120,130,150,0.3)'}` }}>
      {selected ? '● Sélectionnée' : '○ Hors sélection'}
    </span>
  );
}

const allocBtnStyle = (disabled) => ({
  width: 32, height: 32, borderRadius: 8, fontSize: 18, lineHeight: 1,
  fontFamily: 'inherit', fontWeight: 700,
  border: '1.5px solid var(--border-strong)',
  background: disabled ? 'var(--bg-secondary)' : 'var(--card)',
  color: disabled ? 'var(--text-muted)' : 'var(--text)',
  cursor: disabled ? 'not-allowed' : 'pointer',
});

// Le montant dit la conviction : c'est le sens du geste, autant que le total.
const convictionLabel = (m) => (
  m >= 6 ? { txt: 'Conviction maximale', color: '#C0392B' }
    : m >= 4 ? { txt: 'Forte conviction', color: '#E67E22' }
      : m >= 2 ? { txt: 'Conviction', color: '#2E75B6' }
        : { txt: 'Ticket d\'observation', color: '#5A6275' }
);

// Les 15 millions, un jeton par million : le budget se lit d'un coup d'œil,
// sans avoir à faire l'addition.
function BudgetChips({ total, budget }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {Array.from({ length: budget }).map((_, i) => (
        <span key={i} title={`${i + 1} M€`} style={{
          width: 18, height: 18, borderRadius: '50%', fontSize: 9, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: i < total ? 'linear-gradient(145deg, #F4C542, #E0A106)' : 'var(--bg-secondary)',
          border: `1px solid ${i < total ? '#C8880A' : 'var(--border)'}`,
          color: i < total ? '#5A3E00' : 'transparent',
        }}>€</span>
      ))}
    </div>
  );
}

const CL_FULL_LABELS = ['Business Plan', 'Ratio Efficacité Commerciale', 'Solidité Financière',
  'Maturité Financière', 'Marché', 'Innovation', 'Équipe Dirigeante', 'Organisation Commerciale',
  'Notoriété', 'RSE', 'RGPD / Sécurité', 'Conformité'];
const CL_KEYS = ['cl01_bp','cl02_ratio','cl03_solidite_fi','cl04_maturite_fi','cl05_marche','cl06_innovation','cl07_equipe','cl08_org_comm','cl09_notoriete','cl10_rse','cl11_rgpd','cl12_conformite'];

const iconCache = {};
function riskIcon(level) {
  if (iconCache[level]) return iconCache[level];
  const color = level === 'vert' ? '#27AE60' : level === 'orange' ? '#F39C12' : '#E74C3C';
  iconCache[level] = L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>`,
    iconSize: [14, 14], iconAnchor: [7, 7],
  });
  return iconCache[level];
}

function MapBoundsUpdater({ participants }) {
  const map = useMap();
  useEffect(() => {
    try {
      const pts = participants.filter(p => p.latitude && p.longitude);
      if (pts.length > 0 && map && map.getContainer()) {
        const bounds = L.latLngBounds(pts.map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]));
        setTimeout(() => {
          try { map.invalidateSize(); map.fitBounds(bounds, { padding: [30, 30] }); } catch(e) {}
        }, 100);
      }
    } catch(e) {}
  }, [participants, map]);
  return null;
}

export default function JurySpace() {
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [ranking, setRanking] = useState(null);
  const [selectedFiche, setSelectedFiche] = useState(null);
  const [ficheData, setFicheData] = useState(null);
  const [ficheDocs, setFicheDocs] = useState(null);
  const [docError, setDocError] = useState('');
  // Document actuellement ouvert dans la visionneuse (consultation seule).
  const [viewerDoc, setViewerDoc] = useState(null);
  const [tab, setTab] = useState('participants');

  // Filters
  const [searchQ, setSearchQ] = useState('');
  const [filterSecteur, setFilterSecteur] = useState('');
  const [filterRisk, setFilterRisk] = useState('');
  const [filterMinNote, setFilterMinNote] = useState('');

  // Private comments
  const [comments, setComments] = useState({}); // { startupId: "comment", general: "comment" }
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentSaved, setCommentSaved] = useState(false);
  const [generalComment, setGeneralComment] = useState('');
  const [generalSaving, setGeneralSaving] = useState(false);
  const [generalSaved, setGeneralSaved] = useState(false);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [ficheError, setFicheError] = useState('');
  // Coup de cœur : une seule startup par juré et par campagne.
  const [favorite, setFavorite] = useState(null);
  const [favoriteSaving, setFavoriteSaving] = useState(false);
  const toast = useToast();

  // Portefeuille d'investissement virtuel. `alloc` est le brouillon local
  // (startup_id -> millions) ; `portfolio` est l'état renvoyé par le serveur,
  // qui reste l'autorité sur les règles et la clôture.
  const [portfolio, setPortfolio] = useState(null);
  const [alloc, setAlloc] = useState({});
  const [allocDirty, setAllocDirty] = useState(false);
  const [allocSaving, setAllocSaving] = useState(false);

  // Dépouillement, consultable après clôture. Chargé à l'ouverture de l'onglet
  // plutôt qu'avec la campagne : avant clôture la route répond 409, inutile de
  // l'appeler à chaque visite de l'espace jury.
  const [voteResults, setVoteResults] = useState(null);
  const [voteResultsError, setVoteResultsError] = useState('');

  // Ouverture directe d'une fiche depuis la messagerie : /jury?fiche=<id>&thread=<id>.
  // `thread` sert au lien de retour, qui rouvre la conversation d'origine.
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(location.search);
  const ficheParam = urlParams.get('fiche');
  const returnThread = urlParams.get('thread');
  const fromMessages = urlParams.has('thread') || urlParams.get('from') === 'messages';

  useEffect(() => {
    getJuryCampaigns().then(c => {
      setCampaigns(c);
      if (c.length > 0) {
        setActiveCampaign(c[0]);
        // La fiche demandee par lien est ouverte une fois la campagne chargee :
        // openFiche pre-remplit l'appreciation depuis les commentaires, qui ne
        // sont disponibles qu'a la fin de loadCampaign.
        loadCampaign(c[0].id).then(cmts => {
          if (ficheParam) openFiche(parseInt(ficheParam, 10), cmts);
        });
      }
    }).catch(err => {
      // Sans cet etat, une erreur partait uniquement dans la console et la
      // page restait bloquee sur "Chargement..." sans rien dire a l'usager.
      setLoadError(err.message || 'Impossible de charger les campagnes.');
    }).finally(() => setCampaignsLoaded(true));
  }, []);

  const loadCampaign = async (id) => {
    const [p, r] = await Promise.all([getCampaignParticipants(id), getCampaignRanking(id)]);
    setParticipants(p);
    setRanking(r);
    // Le coup de cœur porte son propre statut de sélection, relu ici : la
    // startup distinguée a pu sortir de la sélection depuis l'attribution.
    if (COUP_DE_COEUR) {
      try { setFavorite((await getJuryFavorite(id)).favorite); } catch (e) { console.error(e); }
    }
    try {
      const pf = await getJuryPortfolio(id);
      setPortfolio(pf);
      setAlloc(Object.fromEntries(pf.allocations.map(a => [a.startup_id, a.amount])));
      setAllocDirty(false);
    } catch (e) { console.error(e); }
    // Load private comments
    try {
      const cmts = await getJuryComments(id);
      const map = {};
      cmts.forEach(c => {
        if (c.startup_id) map[c.startup_id] = c.comment || '';
        else map['general'] = c.comment || '';
      });
      setComments(map);
      setGeneralComment(map['general'] || '');
      return map;
    } catch (e) { console.error(e); }
    return {};
  };

  const openFiche = async (startupId, commentsMap) => {
    let data;
    try {
      data = await getParticipantFiche(startupId);
    } catch (err) {
      // Cas typique depuis la messagerie : la startup a ete retiree de la
      // selection soumise au jury apres l'envoi du message. On le dit, au lieu
      // d'une erreur silencieuse dans la console.
      setFicheError(err.message || "Cette fiche n'est pas accessible.");
      setSelectedFiche(null);
      return;
    }
    setFicheError('');
    setFicheData(data);
    // Les documents sont charges a part : un incident de stockage ne doit pas
    // empecher l'affichage de la fiche elle-meme.
    setFicheDocs(null); setDocError('');
    getParticipantDocuments(startupId)
      .then(setFicheDocs)
      .catch(err => setDocError(err.message));
    setSelectedFiche(startupId);
    setCommentDraft((commentsMap || comments)[startupId] || '');
    setCommentSaved(false);
    setTab('participants');
    // Ouverture depuis un lien : amener la fiche a l'ecran.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Consultation d'un document : ouverture dans la visionneuse interne. Le
  // fichier source n'est jamais transmis au navigateur — le serveur renvoie
  // des images de page — donc rien a enregistrer depuis le poste client.
  const openDocument = (doc) => { setDocError(''); setViewerDoc(doc); };

  // Memoises : DocumentViewer relance l'apercu quand loadPreview change, une
  // fonction recreee a chaque rendu bouclerait indefiniment.
  const viewerLoadPreview = useCallback(
    () => getParticipantDocumentPreview(selectedFiche, viewerDoc.id),
    [selectedFiche, viewerDoc],
  );
  const viewerPageUrl = useCallback(
    (p) => getParticipantDocumentPageUrl(selectedFiche, viewerDoc.id, p),
    [selectedFiche, viewerDoc],
  );

  // Filigrane nominatif : une capture d'ecran reste attribuable a son auteur.
  const juryUser = getStoredUser() || {};
  const juryWatermark = [
    `${juryUser.first_name || ''} ${juryUser.last_name || ''}`.trim() || juryUser.email,
    new Date().toLocaleDateString('fr-FR'),
  ].filter(Boolean).join(' — ');

  const handleSaveComment = async (startupId) => {
    if (!activeCampaign) return;
    setCommentSaving(true);
    try {
      await saveJuryStartupComment(activeCampaign.id, startupId, commentDraft.trim());
      setComments(prev => ({ ...prev, [startupId]: commentDraft.trim() }));
      setCommentSaved(true);
      setTimeout(() => setCommentSaved(false), 2000);
    } catch (e) { console.error(e); }
    setCommentSaving(false);
  };

  const handleDeleteComment = async (startupId) => {
    if (!activeCampaign) return;
    setCommentSaving(true);
    try {
      await saveJuryStartupComment(activeCampaign.id, startupId, '');
      setComments(prev => { const n = { ...prev }; delete n[startupId]; return n; });
      setCommentDraft('');
      setCommentSaved(true);
      setTimeout(() => setCommentSaved(false), 2000);
    } catch (e) { console.error(e); }
    setCommentSaving(false);
  };

  const handleSaveGeneral = async () => {
    if (!activeCampaign) return;
    setGeneralSaving(true);
    try {
      await saveJuryGeneralComment(activeCampaign.id, generalComment.trim());
      setComments(prev => ({ ...prev, general: generalComment.trim() }));
      setGeneralSaved(true);
      setTimeout(() => setGeneralSaved(false), 2000);
    } catch (e) { console.error(e); }
    setGeneralSaving(false);
  };

  const [pdfExporting, setPdfExporting] = useState(false);

  const exportPDF = async () => {
    if (!ranking || !activeCampaign) return;
    setPdfExporting(true);
    try {
      // Fetch dashboard stats
      const stats = await getTrophyStats();

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentW = pageW - 2 * margin;
      let y = 15;

      // Helper: check page break
      const needPage = (need) => { if (y + need > pageH - 15) { doc.addPage(); y = 15; } };

      // Helper: section title
      const sectionTitle = (title) => {
        needPage(20);
        doc.setTextColor(50, 50, 50);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(title, margin, y);
        y += 8;
      };

      // Helper: draw a donut chart
      const drawDonut = (cx, cy, outerR, innerR, slices, legendX, legendY) => {
        const total = slices.reduce((s, sl) => s + sl.value, 0);
        if (total === 0) return;
        let startAngle = -Math.PI / 2;
        slices.forEach(sl => {
          const sliceAngle = (sl.value / total) * 2 * Math.PI;
          const endAngle = startAngle + sliceAngle;
          // Draw arc as filled path using small segments
          doc.setFillColor(sl.r, sl.g, sl.b);
          const steps = Math.max(20, Math.round(sliceAngle * 30));
          const points = [[cx, cy]];
          for (let i = 0; i <= steps; i++) {
            const a = startAngle + (sliceAngle * i) / steps;
            points.push([cx + outerR * Math.cos(a), cy + outerR * Math.sin(a)]);
          }
          // Draw filled triangle fan
          for (let i = 1; i < points.length - 1; i++) {
            doc.triangle(points[0][0], points[0][1], points[i][0], points[i][1], points[i + 1][0], points[i + 1][1], 'F');
          }
          startAngle = endAngle;
        });
        // White center hole
        doc.setFillColor(255, 255, 255);
        const holeSteps = 60;
        for (let i = 0; i < holeSteps; i++) {
          const a1 = (2 * Math.PI * i) / holeSteps;
          const a2 = (2 * Math.PI * (i + 1)) / holeSteps;
          doc.triangle(cx, cy, cx + innerR * Math.cos(a1), cy + innerR * Math.sin(a1), cx + innerR * Math.cos(a2), cy + innerR * Math.sin(a2), 'F');
        }
        // Total in center
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(50, 50, 50);
        doc.text(String(total), cx, cy + 2, { align: 'center' });
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        doc.text('total', cx, cy + 6, { align: 'center' });
        // Legend
        let ly = legendY;
        slices.forEach(sl => {
          doc.setFillColor(sl.r, sl.g, sl.b);
          doc.rect(legendX, ly - 2.5, 4, 4, 'F');
          doc.setFontSize(8);
          doc.setTextColor(60, 60, 60);
          doc.setFont('helvetica', 'normal');
          doc.text(`${sl.label} (${sl.value})`, legendX + 6, ly + 0.5);
          ly += 6;
        });
      };

      // Helper: draw vertical bar chart
      const drawBarChart = (x0, y0, w, h, bars) => {
        const maxVal = Math.max(...bars.map(b => b.value), 1);
        const barW = Math.min(18, (w - 10) / bars.length - 2);
        const gap = (w - bars.length * barW) / (bars.length + 1);
        // Y axis
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.2);
        doc.line(x0, y0, x0, y0 + h);
        doc.line(x0, y0 + h, x0 + w, y0 + h);
        // Grid lines
        for (let i = 0; i <= 4; i++) {
          const gy = y0 + h - (h * i) / 4;
          doc.setDrawColor(230, 230, 230);
          doc.line(x0, gy, x0 + w, gy);
          doc.setFontSize(6);
          doc.setTextColor(150, 150, 150);
          doc.text(String(Math.round((maxVal * i) / 4)), x0 - 2, gy + 1, { align: 'right' });
        }
        bars.forEach((b, i) => {
          const bx = x0 + gap + i * (barW + gap);
          const bh = (b.value / maxVal) * (h - 4);
          doc.setFillColor(b.r, b.g, b.b);
          doc.roundedRect(bx, y0 + h - bh, barW, bh, 1, 1, 'F');
          // Value on top
          doc.setFontSize(7);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(b.r, b.g, b.b);
          doc.text(String(b.value), bx + barW / 2, y0 + h - bh - 2, { align: 'center' });
          // Label below
          doc.setFontSize(5.5);
          doc.setTextColor(80, 80, 80);
          doc.setFont('helvetica', 'normal');
          const lbl = b.label.length > 10 ? b.label.substring(0, 10) + '.' : b.label;
          doc.text(lbl, bx + barW / 2, y0 + h + 4, { align: 'center' });
        });
      };

      // Helper: horizontal bar chart
      const drawHBarChart = (x0, y0, w, bars) => {
        const maxVal = Math.max(...bars.map(b => b.value), 1);
        const barH = 8;
        const gap = 3;
        bars.forEach((b, i) => {
          const by = y0 + i * (barH + gap);
          const bw = (b.value / maxVal) * (w - 60);
          // Label
          doc.setFontSize(8);
          doc.setTextColor(60, 60, 60);
          doc.setFont('helvetica', 'normal');
          doc.text(b.label, x0, by + barH / 2 + 1);
          // Bar
          doc.setFillColor(b.r, b.g, b.b);
          doc.roundedRect(x0 + 50, by, Math.max(bw, 2), barH, 2, 2, 'F');
          // Value
          doc.setFontSize(7);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(80, 80, 80);
          doc.text(`${b.value} (${b.pct}%)`, x0 + 52 + bw, by + barH / 2 + 1);
        });
        return bars.length * (barH + gap);
      };

      // =====================================================
      // PAGE 1: Header + KPIs + Charts
      // =====================================================

      // --- Header ---
      doc.setFillColor(75, 0, 130);
      doc.rect(0, 0, pageW, 38, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Trophées de l’Innovation', margin, 18);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(activeCampaign.name, margin, 27);
      const today = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
      doc.setFontSize(9);
      doc.text('Généré le ' + today, margin, 34);
      y = 46;

      // --- KPIs row ---
      sectionTitle('Synthèse');
      const qit = stats?.qit_stats || {};
      const kpis = [
        { label: 'Participants', value: String(stats?.total || ranking.total), color: [75, 0, 130] },
        { label: 'Note ISI moy.', value: (stats?.averages?.avg_sectorielle || ranking.average.toFixed(2)) + '/10', color: [230, 126, 34] },
        { label: 'Note Pivot moy.', value: (stats?.averages?.avg_pivot || '-') + '/10', color: [46, 117, 182] },
        { label: 'QIT complétion', value: Math.max(0, Math.min(100, qit.avg_progress || 0)) + '%', color: [39, 174, 96] },
      ];
      const boxW = (contentW - 3 * 4) / 4;
      kpis.forEach((kpi, i) => {
        const bx = margin + i * (boxW + 4);
        doc.setFillColor(245, 245, 255);
        doc.roundedRect(bx, y, boxW, 20, 3, 3, 'F');
        doc.setFontSize(15);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...kpi.color);
        doc.text(kpi.value, bx + boxW / 2, y + 10, { align: 'center' });
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(120, 120, 120);
        doc.text(kpi.label, bx + boxW / 2, y + 17, { align: 'center' });
      });
      y += 28;

      // --- QIT Progress ---
      sectionTitle('Avancement du questionnaire QIT');
      const qitItems = [
        { label: 'Complets (≥ 80%)', value: qit.qit_complete || 0, r: 39, g: 174, b: 96 },
        { label: 'Partiels (< 80%)', value: qit.qit_partial || 0, r: 243, g: 156, b: 18 },
        { label: 'Non commencés', value: qit.qit_empty || 0, r: 231, g: 76, b: 60 },
      ];
      const qitBoxW = (contentW - 2 * 6) / 3;
      qitItems.forEach((item, i) => {
        const bx = margin + i * (qitBoxW + 6);
        doc.setFillColor(item.r, item.g, item.b, 0.1);
        doc.setDrawColor(item.r, item.g, item.b);
        doc.setLineWidth(0.5);
        doc.roundedRect(bx, y, qitBoxW, 16, 3, 3, 'FD');
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(item.r, item.g, item.b);
        doc.text(String(item.value), bx + qitBoxW / 2, y + 8, { align: 'center' });
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(item.label, bx + qitBoxW / 2, y + 14, { align: 'center' });
      });
      y += 24;

      // --- Distribution des scores ISI (Bar chart) ---
      sectionTitle('Distribution des scores ISI (participants)');
      if (stats?.score_distribution) {
        const scoreColors = { 'Excellent': [39, 174, 96], 'Bon': [46, 204, 113], 'Moyen': [243, 156, 18], 'Faible': [230, 126, 34], 'Critique': [231, 76, 60] };
        const bars = stats.score_distribution.map(s => {
          const key = Object.keys(scoreColors).find(k => s.tranche.includes(k)) || 'Moyen';
          const [r, g, b] = scoreColors[key];
          return { label: s.tranche, value: parseInt(s.count), r, g, b };
        });
        drawBarChart(margin + 8, y, contentW - 8, 45, bars);
        y += 56;
      }

      // --- Two donuts side by side: Risk + Secteur ---
      needPage(80);
      // Risk donut (left)
      doc.setTextColor(50, 50, 50);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Indicateurs de risque', margin, y);
      doc.text('Répartition sectorielle', margin + contentW / 2 + 5, y);
      y += 4;

      if (stats?.by_risk) {
        const riskColors = { vert: [39, 174, 96], orange: [243, 156, 18], rouge: [231, 76, 60] };
        const riskLabels = { vert: 'Résilient', orange: 'Vigilance', rouge: 'Critique' };
        const riskSlices = stats.by_risk.map(r => {
          const [rv, gv, bv] = riskColors[r.risk_level] || [150, 150, 150];
          return { label: riskLabels[r.risk_level] || r.risk_level, value: parseInt(r.count), r: rv, g: gv, b: bv };
        });
        drawDonut(margin + 25, y + 22, 18, 10, riskSlices, margin + 48, y + 8);
      }

      // Secteur donut (right)
      if (stats?.by_secteur) {
        const sColors = [[124, 58, 237], [230, 126, 34], [39, 174, 96], [231, 76, 60], [155, 89, 182], [26, 188, 156], [243, 156, 18], [52, 152, 219], [149, 165, 166]];
        const sectSlices = stats.by_secteur.map((s, i) => {
          const [r, g, b] = sColors[i % sColors.length];
          return { label: s.secteur, value: parseInt(s.count), r, g, b };
        });
        const rightX = margin + contentW / 2 + 5;
        drawDonut(rightX + 25, y + 22, 18, 10, sectSlices, rightX + 48, y + 2);
      }
      y += 52;

      // --- Stade de développement (horizontal bars) ---
      if (stats?.by_stade && stats.by_stade.length > 0) {
        needPage(15 + stats.by_stade.length * 11);
        sectionTitle('Par stade de développement');
        const stadeTotal = stats.by_stade.reduce((s, st) => s + parseInt(st.count), 0);
        const stadeBars = stats.by_stade.map(s => ({
          label: s.stade_dev || 'N/A',
          value: parseInt(s.count),
          pct: stadeTotal > 0 ? (parseInt(s.count) / stadeTotal * 100).toFixed(0) : '0',
          r: 230, g: 126, b: 34,
        }));
        const hbarH = drawHBarChart(margin, y, contentW, stadeBars);
        y += hbarH + 8;
      }

      // --- Moyennes par cluster ---
      needPage(45);
      sectionTitle('Moyennes par cluster');
      if (stats?.averages) {
        const clusterAvgs = [
          { key: 'avg_bp', label: 'Business Plan' }, { key: 'avg_ratio', label: 'Ratio Efficacité' },
          { key: 'avg_solidite', label: 'Solidité Fi.' }, { key: 'avg_maturite', label: 'Maturité Fi.' },
          { key: 'avg_marche', label: 'Marché' }, { key: 'avg_innovation', label: 'Innovation' },
          { key: 'avg_equipe', label: 'Équipe' }, { key: 'avg_org_comm', label: 'Org. Comm.' },
          { key: 'avg_notoriete', label: 'Notoriété' }, { key: 'avg_rse', label: 'RSE' },
          { key: 'avg_rgpd', label: 'RGPD' }, { key: 'avg_conformite', label: 'Conformité' },
        ];
        const cBoxW = (contentW - 3 * 3) / 4;
        const cBoxH = 14;
        clusterAvgs.forEach((c, i) => {
          const col = i % 4;
          const row = Math.floor(i / 4);
          const bx = margin + col * (cBoxW + 3);
          const by = y + row * (cBoxH + 3);
          const val = parseFloat(stats.averages[c.key]) || 0;
          const color = val > 7 ? [39, 174, 96] : val >= 4 ? [243, 156, 18] : [231, 76, 60];
          doc.setFillColor(248, 245, 255);
          doc.setDrawColor(220, 220, 230);
          doc.setLineWidth(0.3);
          doc.roundedRect(bx, by, cBoxW, cBoxH, 2, 2, 'FD');
          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...color);
          doc.text(val.toFixed(1), bx + cBoxW / 2, by + 7, { align: 'center' });
          doc.setFontSize(6);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100, 100, 100);
          doc.text(c.label, bx + cBoxW / 2, by + 12, { align: 'center' });
        });
        y += 3 * (cBoxH + 3) + 8;
      }

      // =====================================================
      // TABLEAUX: Classement + Détails
      // =====================================================

      // --- Classement Top 20 ---
      needPage(30);
      sectionTitle('Classement ISI (Top 20)');
      const top = ranking.ranking.slice(0, 20);
      const clNames = ['BP', 'Ratio', 'Solidité Fi.', 'Mat. Fi.', 'Marché', 'Innov.', 'Équipe', 'Org.Com.', 'Notor.', 'RSE', 'RGPD', 'Conf.'];
      const rankRows = top.map((r, idx) => [
        idx < 3 ? ['1er', '2e', '3e'][idx] : '#' + r.rank,
        r.name || '',
        r.categorie || r.secteur || '',
        parseFloat(r.note_sectorielle || 0).toFixed(2),
        (r.risk_level || '').charAt(0).toUpperCase() + (r.risk_level || '').slice(1),
      ]);
      doc.autoTable({
        startY: y,
        head: [['#', 'Startup', 'Secteur', 'Note ISI', 'Risque']],
        body: rankRows,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 2.5 },
        headStyles: { fillColor: [75, 0, 130], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 12, halign: 'center' },
          2: { cellWidth: 28 },
          3: { cellWidth: 18, halign: 'center', fontStyle: 'bold' },
          4: { cellWidth: 18, halign: 'center' },
        },
        alternateRowStyles: { fillColor: [248, 245, 255] },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 4) {
            const v = (data.cell.text[0] || '').toLowerCase();
            if (v === 'vert') data.cell.styles.textColor = [39, 174, 96];
            else if (v === 'orange') data.cell.styles.textColor = [243, 156, 18];
            else if (v === 'rouge') data.cell.styles.textColor = [231, 76, 60];
          }
          if (data.section === 'body' && data.column.index === 0 && data.row.index < 3) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = [75, 0, 130];
          }
        },
      });
      y = doc.lastAutoTable.finalY + 10;

      // --- Détail des scores par cluster (Top 10) ---
      needPage(30);
      sectionTitle('Détail des scores par cluster (Top 10)');
      const detailTop = ranking.ranking.slice(0, 10);
      const detailRows = detailTop.map((r, idx) => {
        const row = [idx < 3 ? ['1er', '2e', '3e'][idx] : '#' + r.rank, r.name || ''];
        CL_KEYS.forEach(k => row.push(parseFloat(r[k] || 0).toFixed(1)));
        row.push(parseFloat(r.note_sectorielle || 0).toFixed(2));
        return row;
      });
      doc.autoTable({
        startY: y,
        head: [['#', 'Startup', ...clNames, 'ISI']],
        body: detailRows,
        margin: { left: margin, right: margin },
        styles: { fontSize: 5.5, cellPadding: 1.5 },
        headStyles: { fillColor: [75, 0, 130], textColor: 255, fontStyle: 'bold', fontSize: 5.5 },
        columnStyles: {
          0: { cellWidth: 8, halign: 'center' },
          1: { cellWidth: 28 },
          14: { fontStyle: 'bold' },
        },
        alternateRowStyles: { fillColor: [248, 245, 255] },
      });
      y = doc.lastAutoTable.finalY + 10;

      // --- Liste complète ---
      if (ranking.ranking.length > 20) {
        doc.addPage();
        y = 15;
        sectionTitle('Liste complète des participants (' + ranking.ranking.length + ')');
        const allRows = ranking.ranking.map(r => [
          '#' + r.rank, r.name || '', r.categorie || r.secteur || '',
          parseFloat(r.note_sectorielle || 0).toFixed(2),
          (r.risk_level || '').charAt(0).toUpperCase() + (r.risk_level || '').slice(1),
        ]);
        doc.autoTable({
          startY: y,
          head: [['#', 'Startup', 'Secteur', 'Note ISI', 'Risque']],
          body: allRows,
          margin: { left: margin, right: margin },
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [75, 0, 130], textColor: 255, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 10, halign: 'center' },
            3: { cellWidth: 18, halign: 'center', fontStyle: 'bold' },
            4: { cellWidth: 18, halign: 'center' },
          },
          alternateRowStyles: { fillColor: [248, 245, 255] },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index === 4) {
              const v = (data.cell.text[0] || '').toLowerCase();
              if (v === 'vert') data.cell.styles.textColor = [39, 174, 96];
              else if (v === 'orange') data.cell.styles.textColor = [243, 156, 18];
              else if (v === 'rouge') data.cell.styles.textColor = [231, 76, 60];
            }
          },
        });
      }

      // --- Footer on all pages ---
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(160, 160, 160);
        doc.text('Plateforme ISI — Investance Partners — Confidentiel', margin, pageH - 6);
        doc.text('Page ' + i + '/' + totalPages, pageW - margin, pageH - 6, { align: 'right' });
      }

      doc.save('Trophees_Innovation_' + (activeCampaign.name || '').replace(/[^a-zA-Z0-9]/g, '_') + '.pdf');
    } catch (err) {
      console.error('PDF export error:', err);
    }
    setPdfExporting(false);
  };

  // ===================== EXPORT PDF D'UNE FICHE STARTUP =====================
  // Distinct de exportPDF (synthèse de la campagne) : reprend le contenu de la
  // fiche ouverte, radar de résilience compris. Le radar est capturé depuis le
  // canvas Chart.js affiché — c'est exactement le graphique que voit le juré.
  const radarRef = useRef(null);
  const [fichePdfExporting, setFichePdfExporting] = useState(false);

  const exportFichePDF = async () => {
    if (!ficheData) return;
    setFichePdfExporting(true);
    try {
      const s = ficheData.startup;
      const name = s.startup_name || s.name || 'Startup';
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentW = pageW - 2 * margin;
      let y = 15;

      const needPage = (need) => { if (y + need > pageH - 18) { doc.addPage(); y = 18; } };
      const sectionTitle = (title) => {
        needPage(16);
        doc.setTextColor(50, 50, 50);
        doc.setFontSize(12.5);
        doc.setFont('helvetica', 'bold');
        doc.text(title, margin, y);
        doc.setDrawColor(74, 113, 229);
        doc.setLineWidth(0.6);
        doc.line(margin, y + 2, margin + 30, y + 2);
        y += 8;
      };
      // Paragraphe avec saut de page automatique, ligne par ligne : un résumé
      // exécutif long ne doit pas déborder du bas de la page.
      const paragraph = (text, size = 9.5, color = [70, 70, 70]) => {
        doc.setFontSize(size);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...color);
        const lines = doc.splitTextToSize(String(text), contentW);
        const lh = size * 0.45;
        lines.forEach(line => { needPage(lh + 1); doc.text(line, margin, y); y += lh; });
        y += 3;
      };
      const rgb = (hex) => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));

      // --- En-tête ---
      doc.setFillColor(27, 30, 56);
      doc.rect(0, 0, pageW, 36, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(19);
      doc.setFont('helvetica', 'bold');
      doc.text(name, margin, 16);
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'normal');
      const sub = [s.categorie, s.sous_domaine, s.stade_dev].filter(Boolean).join('  ·  ');
      if (sub) doc.text(sub, margin, 24);
      const today = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
      doc.setFontSize(8);
      doc.setTextColor(170, 180, 210);
      doc.text(`Fiche candidat — ${activeCampaign ? activeCampaign.name : 'Trophées de l’Innovation'} — générée le ${today}`, margin, 31);
      y = 46;

      // --- Score ISI : 3 cartouches ---
      const isi = s.note_sectorielle != null ? parseFloat(s.note_sectorielle) : null;
      const pivot = s.note_pivot != null ? parseFloat(s.note_pivot) : null;
      const riskLabel = { vert: 'Résilient', orange: 'Vigilance', rouge: 'Critique' }[s.risk_level] || '—';
      const riskColor = { vert: [39, 174, 96], orange: [243, 156, 18], rouge: [231, 76, 60] }[s.risk_level] || [120, 120, 120];
      const kpis = [
        { label: 'Score ISI', value: isi != null ? `${isi.toFixed(2)}/10` : '—', color: isi != null ? rgb(scoreColor(isi)) : [120, 120, 120] },
        { label: 'Note Pivot', value: pivot != null ? `${pivot.toFixed(2)}/10` : '—', color: [46, 117, 182] },
        { label: 'Niveau de risque', value: riskLabel, color: riskColor },
      ];
      const boxW = (contentW - 2 * 5) / 3;
      kpis.forEach((k, i) => {
        const bx = margin + i * (boxW + 5);
        doc.setFillColor(245, 246, 252);
        doc.roundedRect(bx, y, boxW, 20, 3, 3, 'F');
        doc.setFontSize(15);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...k.color);
        doc.text(k.value, bx + boxW / 2, y + 10, { align: 'center' });
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(120, 120, 120);
        doc.text(k.label, bx + boxW / 2, y + 16.5, { align: 'center' });
      });
      y += 27;

      if (s.top_force || s.top_faiblesse) {
        doc.setFontSize(9.5);
        if (s.top_force) {
          doc.setFont('helvetica', 'bold'); doc.setTextColor(39, 174, 96);
          doc.text('Point fort :', margin, y);
          doc.setFont('helvetica', 'normal'); doc.text(String(s.top_force), margin + 20, y);
          y += 5.5;
        }
        if (s.top_faiblesse) {
          doc.setFont('helvetica', 'bold'); doc.setTextColor(231, 76, 60);
          doc.text("Point d'attention :", margin, y);
          doc.setFont('helvetica', 'normal'); doc.text(String(s.top_faiblesse), margin + 31, y);
          y += 5.5;
        }
        y += 3;
      }

      // --- Radar + notes par cluster, côte à côte ---
      sectionTitle('Radar de résilience et note par cluster (sur 10)');
      const blockTop = y;
      const radarW = 92;
      let radarH = 0;
      const canvas = radarRef.current ? radarRef.current.querySelector('canvas') : null;
      if (canvas) {
        // Fond blanc explicite : le canvas Chart.js est transparent, et certains
        // lecteurs PDF affichent la transparence en noir.
        const flat = document.createElement('canvas');
        flat.width = canvas.width; flat.height = canvas.height;
        const ctx = flat.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, flat.width, flat.height);
        ctx.drawImage(canvas, 0, 0);
        radarH = radarW * (canvas.height / canvas.width);
        doc.addImage(flat.toDataURL('image/png'), 'PNG', margin, blockTop, radarW, radarH);
      } else {
        doc.setFontSize(9); doc.setTextColor(150, 150, 150);
        doc.text('Radar indisponible', margin, blockTop + 10);
        radarH = 20;
      }

      const tableX = margin + radarW + 6;
      doc.autoTable({
        startY: blockTop,
        margin: { left: tableX, right: margin },
        body: CL_KEYS.map((k, i) => {
          const v = s[k];
          return [CL_FULL_LABELS[i], v == null ? '—' : parseFloat(v).toFixed(1)];
        }),
        theme: 'plain',
        styles: { fontSize: 8.5, cellPadding: { top: 1.6, bottom: 1.6, left: 2, right: 2 }, textColor: [80, 80, 80] },
        columnStyles: { 1: { halign: 'right', fontStyle: 'bold', cellWidth: 14 } },
        didParseCell: (data) => {
          if (data.column.index === 1) {
            const v = parseFloat(data.cell.raw);
            data.cell.styles.textColor = Number.isNaN(v) ? [150, 150, 150] : rgb(scoreColor(v));
          }
        },
        didDrawCell: (data) => {
          doc.setDrawColor(230, 232, 240);
          doc.setLineWidth(0.2);
          doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
        },
      });
      y = Math.max(blockTop + radarH, doc.lastAutoTable.finalY) + 8;

      // --- Identité ---
      const identite = [
        ['Secteur', s.categorie], ['Sous-domaine', s.sous_domaine], ['Stade', s.stade_dev],
        ['Effectif', s.effectif], ['Maturité', s.maturite], ['Année de création', s.annee_creation],
        ['Région', s.region], ['Site web', s.site_web], ['Adresse', s.adresse],
      ].filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '');
      if (identite.length) {
        sectionTitle('Identité');
        doc.autoTable({
          startY: y,
          margin: { left: margin, right: margin },
          body: identite.map(([l, v]) => [l, String(v)]),
          theme: 'plain',
          styles: { fontSize: 9, cellPadding: 1.8, textColor: [60, 60, 60] },
          columnStyles: { 0: { cellWidth: 38, textColor: [130, 130, 130] }, 1: { fontStyle: 'bold' } },
        });
        y = doc.lastAutoTable.finalY + 8;
      }

      // --- Résumé exécutif ---
      if (s.pitch) {
        sectionTitle('Résumé exécutif');
        paragraph(s.pitch);
        y += 2;
      }

      // --- Documents : présence seulement, jamais le contenu ---
      if (ficheDocs && ficheDocs.documents) {
        sectionTitle(`Documents du dossier (${ficheDocs.available}/${ficheDocs.total} disponibles)`);
        doc.autoTable({
          startY: y,
          margin: { left: margin, right: margin },
          body: ficheDocs.documents.map(d => [d.document_type, d.available ? 'Fourni' : 'Non fourni']),
          theme: 'plain',
          styles: { fontSize: 9, cellPadding: 1.8, textColor: [60, 60, 60] },
          columnStyles: { 1: { halign: 'right', cellWidth: 30, fontStyle: 'bold' } },
          didParseCell: (data) => {
            if (data.column.index === 1) {
              data.cell.styles.textColor = data.cell.raw === 'Fourni' ? [39, 174, 96] : [178, 106, 0];
            }
          },
        });
        y = doc.lastAutoTable.finalY + 3;
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        needPage(6);
        doc.text('Les documents sont consultables uniquement sur la plateforme, dans la visionneuse sécurisée.', margin, y);
        y += 9;
      }

      // --- Informations complémentaires ---
      const complements = [
        ['Fondateur(s)', s.fondateurs], ['Investisseur(s)', s.investisseurs_details],
        ['Levée(s) de fonds', s.levees_details], ['Immatriculation', s.immatriculation],
        ['LinkedIn', s.linkedin], ['Email', s.contact_email],
      ].filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '');
      if (complements.length) {
        sectionTitle('Informations complémentaires');
        doc.autoTable({
          startY: y,
          margin: { left: margin, right: margin },
          body: complements.map(([l, v]) => [l, String(v)]),
          theme: 'plain',
          styles: { fontSize: 9, cellPadding: 1.8, textColor: [60, 60, 60] },
          columnStyles: { 0: { cellWidth: 38, textColor: [130, 130, 130] } },
        });
        y = doc.lastAutoTable.finalY + 8;
      }

      // --- Mon appréciation : incluse seulement si le juré en a saisi une ---
      const appreciation = (commentDraft || '').trim();
      if (appreciation) {
        sectionTitle('Mon appréciation (personnelle)');
        paragraph(appreciation, 9.5, [74, 20, 140]);
      }

      // --- Pied de page ---
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(160, 160, 160);
        doc.text('Trophées de l’Innovation LBP AM — Document confidentiel', margin, pageH - 7);
        doc.text(`Page ${i}/${totalPages}`, pageW - margin, pageH - 7, { align: 'right' });
      }

      const safeName = name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9]+/g, '_');
      doc.save(`Fiche_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('Export PDF fiche :', err);
      window.alert("La fiche n'a pas pu être exportée en PDF.");
    }
    setFichePdfExporting(false);
  };

  // Compute filtered list
  const secteurs = useMemo(() => {
    const fromData = participants.map(p => p.secteur || p.categorie).filter(Boolean);
    const reference = ['Fintech','Insurtech','Regtech','Biotech','Greentech','Medtech','SaaS','HealthTech','Immotech'];
    return [...new Set([...reference, ...fromData])].sort();
  }, [participants]);

  // Deux périmètres distincts :
  //   — consultation : toutes les candidatures soumises (onglet Participants) ;
  //   — délibération : la sélection de l'organisateur (onglet Sélection, carte,
  //     et classement ISI, ce dernier étant filtré côté serveur).
  const selected = useMemo(() => participants.filter(p => p.jury_visible), [participants]);

  const baseList = (tab === 'selection' || tab === 'map') ? selected : participants;

  const filtered = useMemo(() => {
    return baseList.filter(p => {
      if (searchQ && !p.name.toLowerCase().includes(searchQ.toLowerCase()) &&
          !(p.sous_domaine || '').toLowerCase().includes(searchQ.toLowerCase())) return false;
      if (filterSecteur && p.secteur !== filterSecteur && p.categorie !== filterSecteur) return false;
      if (filterRisk && p.risk_level !== filterRisk) return false;
      if (filterMinNote && parseFloat(p.note_sectorielle) < parseFloat(filterMinNote)) return false;
      return true;
    });
  }, [baseList, searchQ, filterSecteur, filterRisk, filterMinNote]);

  // ----- Portefeuille d'investissement --------------------------------------
  const RULES = portfolio?.rules || { budget: 15, min_lines: 3, max_lines: 5, min_ticket: 1, max_ticket: 7 };
  const allocTotal = Object.values(alloc).reduce((s, v) => s + (v || 0), 0);
  const allocLines = Object.values(alloc).filter(v => v > 0).length;
  const allocRemaining = RULES.budget - allocTotal;
  const voteLocked = !!portfolio?.locked;

  // Dépouillement chargé à l'ouverture de l'onglet, et rechargé si la campagne
  // change : le résultat d'une campagne ne doit pas rester affiché sous une
  // autre.
  useEffect(() => {
    if (tab !== 'results' || !activeCampaign) return;
    let annule = false;
    setVoteResults(null);
    setVoteResultsError('');
    getJuryVoteResults(activeCampaign.id)
      .then(r => { if (!annule) setVoteResults(r); })
      .catch(e => { if (!annule) setVoteResultsError(e.message || 'Résultat indisponible.'); });
    return () => { annule = true; };
  }, [tab, activeCampaign]);

  // Règles vérifiées en direct : le juré voit ce qui manque pendant qu'il
  // compose, au lieu de l'apprendre en tentant d'enregistrer.
  const allocChecks = [
    { ok: allocLines >= RULES.min_lines && allocLines <= RULES.max_lines,
      txt: `${RULES.min_lines} à ${RULES.max_lines} startups (${allocLines})` },
    { ok: Object.values(alloc).every(v => !v || (v >= RULES.min_ticket && v <= RULES.max_ticket)),
      txt: `${RULES.min_ticket} à ${RULES.max_ticket} M€ par startup` },
    { ok: allocRemaining === 0, txt: `${RULES.budget} M€ entièrement investis` },
  ];
  const allocComplete = allocChecks.every(c => c.ok);

  const setAmount = (startupId, next) => {
    if (voteLocked) return;
    const current = alloc[startupId] || 0;
    let value = Math.max(0, Math.min(RULES.max_ticket, next));
    if (value > current && value - current > allocRemaining) {
      // On ne bloque pas sèchement : on donne ce qui reste, et on le dit.
      value = current + Math.max(0, allocRemaining);
      if (value === current) { toast.error('Votre budget est entièrement investi.'); return; }
    }
    if (value > 0 && current === 0 && allocLines >= RULES.max_lines) {
      toast.error(`Votre portefeuille compte déjà ${RULES.max_lines} startups, le maximum.`);
      return;
    }
    if (value > 0 && favorite && favorite.startup_id === startupId) {
      toast.error(
        `${favorite.name} est votre coup de cœur : cette distinction s'adresse à une startup `
        + `que vous ne financez pas. Changez d'abord de coup de cœur.`
      );
      return;
    }
    setAlloc(prev => {
      const n = { ...prev };
      if (value > 0) n[startupId] = value; else delete n[startupId];
      return n;
    });
    setAllocDirty(true);
  };

  const handleSavePortfolio = async () => {
    setAllocSaving(true);
    try {
      const res = await saveJuryPortfolio(activeCampaign.id,
        Object.entries(alloc).filter(([, v]) => v > 0)
          .map(([sid, amount]) => ({ startup_id: Number(sid), amount })));
      setPortfolio(res);
      setAlloc(Object.fromEntries(res.allocations.map(a => [a.startup_id, a.amount])));
      setAllocDirty(false);
      toast.success(res.complete
        ? 'Portefeuille enregistré et complet'
        : 'Brouillon enregistré — votre portefeuille n\'est pas encore complet');
    } catch (e) { toast.error(e.message); }
    setAllocSaving(false);
  };

  const handleResetPortfolio = async () => {
    const ok = await toast.confirm('Remettre votre portefeuille à zéro ? Vos 15 M€ seront de nouveau disponibles.');
    if (!ok) return;
    setAlloc({});
    setAllocDirty(true);
  };

  // ----- Coup de cœur -------------------------------------------------------
  // Un seul par juré : désigner une autre startup remplace le précédent, ce qui
  // est demandé explicitement avant d'écrire.
  const handleSetFavorite = async (startupId, startupName) => {
    if (favorite && favorite.startup_id === startupId) {
      const ok = await toast.confirm(
        `${startupName} est votre coup de cœur.\n\nLe retirer ? Vous pourrez en désigner un autre ensuite.`
      );
      if (!ok) return;
      setFavoriteSaving(true);
      try {
        await clearJuryFavorite(activeCampaign.id);
        setFavorite(null);
        toast.success('Coup de cœur retiré');
      } catch (e) { toast.error(e.message); }
      setFavoriteSaving(false);
      return;
    }
    if (favorite) {
      const ok = await toast.confirm(
        `Votre coup de cœur est déjà attribué à ${favorite.name}.\n\n`
        + `Le transférer à ${startupName} ? ${favorite.name} ne sera plus votre coup de cœur.`
      );
      if (!ok) return;
    }
    setFavoriteSaving(true);
    try {
      const res = await setJuryFavorite(activeCampaign.id, startupId);
      const isSelected = (participants.find(p => p.id === startupId) || {}).jury_visible;
      setFavorite({ startup_id: startupId, name: startupName, jury_visible: !!isSelected });
      toast.success(res.replaced
        ? `Coup de cœur transféré de ${res.replaced.name} à ${startupName}`
        : `${startupName} est votre coup de cœur`);
    } catch (e) { toast.error(e.message); }
    setFavoriteSaving(false);
  };

  // Trois etats distincts : chargement en cours, echec de l'appel, ou appel
  // reussi mais aucune campagne accessible (membre du jury non rattache).
  // Avant, ces trois cas affichaient le meme "Chargement..." indefiniment.
  if (!activeCampaign) {
    if (!campaignsLoaded) return <div className="loading">Chargement de l'espace jury...</div>;
    return (
      <div style={{ padding: '48px 0' }}>
        <div className="container" style={{ maxWidth: 640 }}>
          <div className="card" style={{ textAlign: 'center', padding: 32 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>{loadError ? '⚠️' : '🗳️'}</div>
            <h2 style={{ fontSize: 18, marginBottom: 12 }}>
              {loadError ? 'Espace jury indisponible' : 'Aucune campagne accessible'}
            </h2>
            <p style={{ color: 'var(--text-light)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              {loadError
                ? loadError
                : "Votre compte jury n'est rattaché à aucune campagne des Trophées. "
                  + "Contactez l'administrateur de la plateforme pour qu'il vous donne accès à la campagne en cours."}
            </p>
            <button onClick={() => window.location.reload()} className="btn btn-outline" style={{ fontSize: 13 }}>
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 0' }}>
      {/* Visionneuse en consultation seule, montee au-dessus de tout le reste.
          useCallback sur loadPreview : sans cela, une nouvelle fonction a chaque
          rendu relancerait l'aperçu en boucle. */}
      {viewerDoc && (
        <DocumentViewer
          title={viewerDoc.document_type}
          subtitle={ficheData ? (ficheData.startup.startup_name || ficheData.startup.name) : ''}
          watermark={juryWatermark}
          loadPreview={viewerLoadPreview}
          pageUrl={viewerPageUrl}
          onClose={() => setViewerDoc(null)}
        />
      )}
      <div className="container">
        {fromMessages && (
          <button type="button"
            onClick={() => navigate(returnThread ? `/jury/messages?thread=${returnThread}` : '/jury/messages')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 16,
              padding: '8px 16px', borderRadius: 8, border: '1px solid #CFD8F5', background: '#EEF2FD',
              color: 'var(--primary)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600 }}>
            ← Retour à la messagerie
          </button>
        )}
        {ficheError && (
          <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', color: '#8D6E20',
            padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13.5 }}>
            {ficheError}
          </div>
        )}
        <div className="page-head">
          <div>
            <h1 style={{ marginBottom: 4 }}>Espace Jury — Trophées de l'Innovation</h1>
            <p style={{ color: 'var(--text-light)', fontSize: 14 }}>{activeCampaign.name}</p>
            <p style={{ color: 'var(--text-light)', fontSize: 12, marginTop: 2 }}>
              Seuls les dossiers <strong>soumis</strong> par les startups participantes sont présentés à l'évaluation.
            </p>
          </div>
          <div className="page-head-actions">
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary)' }}>{participants.length}</div>
              <div style={{ fontSize: 13, color: 'var(--text-light)' }}>dossiers soumis</div>
              <div style={{ fontSize: 12, color: 'var(--text-light)' }}>
                dont <strong style={{ color: '#1E8449' }}>{selected.length}</strong> sélectionné{selected.length > 1 ? 's' : ''}
              </div>
            </div>
            <button onClick={exportPDF} disabled={pdfExporting || !ranking}
              className="btn btn-outline" style={{ fontSize: 12, padding: '10px 16px', whiteSpace: 'nowrap' }}>
              {pdfExporting ? 'Génération...' : 'Exporter PDF'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="tab-bar">
          {[
            { key: 'participants', label: `Participants (${participants.length})` },
            { key: 'selection', label: `Sélection (${selected.length})` },
            // Onglet présent seulement une fois le vote clôturé : avant, voir où
            // se portent les voix des autres permettrait de placer les siennes
            // en conséquence, et ce ne serait plus le même vote.
            ...(voteLocked ? [{ key: 'results', label: 'Résultat du vote' }] : []),
            { key: 'ranking', label: 'Classement ISI' },
            { key: 'map', label: 'Carte de France' },
            { key: 'notes', label: 'Mes appréciations' },
          ].map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setSelectedFiche(null); }}
              style={{ padding: '12px 24px', border: 'none', background: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
                borderBottom: `3px solid ${tab === t.key ? 'var(--accent)' : 'transparent'}`,
                color: tab === t.key ? 'var(--accent)' : 'var(--text-light)', marginBottom: -2 }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters (participants, sélection et carte : mêmes critères) */}
        {(tab === 'participants' || tab === 'selection' || tab === 'map') && !selectedFiche && (
          <div className="card" style={{ marginBottom: 20, padding: 16 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label className="form-label">Recherche</label>
                <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)}
                  className="form-input" placeholder="Nom, sous-domaine..." style={{ fontSize: 13 }} />
              </div>
              <div style={{ flex: '0 1 160px' }}>
                <label className="form-label">Secteur</label>
                <select value={filterSecteur} onChange={e => setFilterSecteur(e.target.value)} className="form-input" style={{ fontSize: 13 }}>
                  <option value="">Tous</option>
                  {secteurs.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ flex: '0 1 140px' }}>
                <label className="form-label">Risque</label>
                <select value={filterRisk} onChange={e => setFilterRisk(e.target.value)} className="form-input" style={{ fontSize: 13 }}>
                  <option value="">Tous</option>
                  <option value="vert">Vert</option>
                  <option value="orange">Orange</option>
                  <option value="rouge">Rouge</option>
                </select>
              </div>
              <div style={{ flex: '0 1 140px' }}>
                <label className="form-label">Note ISI min.</label>
                <select value={filterMinNote} onChange={e => setFilterMinNote(e.target.value)} className="form-input" style={{ fontSize: 13 }}>
                  <option value="">Toutes</option>
                  <option value="8">8+ (Excellent)</option>
                  <option value="7">7+ (Bon)</option>
                  <option value="6">6+</option>
                  <option value="5">5+</option>
                </select>
              </div>
              {(searchQ || filterSecteur || filterRisk || filterMinNote) && (
                <button onClick={() => { setSearchQ(''); setFilterSecteur(''); setFilterRisk(''); setFilterMinNote(''); }}
                  className="btn btn-outline" style={{ fontSize: 12, padding: '8px 14px', alignSelf: 'end' }}>
                  Réinitialiser
                </button>
              )}
            </div>
            {filtered.length !== baseList.length && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-light)' }}>
                {filtered.length} startup{filtered.length > 1 ? 's' : ''} sur {baseList.length} après filtrage
              </div>
            )}
          </div>
        )}

        {/* Fiche modal */}
        {selectedFiche && ficheData && (
          <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid var(--accent)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <LogoImg src={ficheData.startup.logo_url} name={ficheData.startup.startup_name || ficheData.startup.name} size={48} />
                <div>
                  <h2 style={{ marginBottom: 4 }}>{ficheData.startup.startup_name || ficheData.startup.name}</h2>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {ficheData.startup.categorie && <span className="badge badge-secteur">{ficheData.startup.categorie}</span>}
                    {ficheData.startup.stade_dev && <span className="badge">{ficheData.startup.stade_dev}</span>}
                    {ficheData.startup.risk_level && <RiskBadge level={ficheData.startup.risk_level} />}
                    <SelectionBadge selected={ficheData.jury_visible} size={11} />
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                {COUP_DE_COEUR && (() => {
                  const isFav = favorite && favorite.startup_id === selectedFiche;
                  const name = ficheData.startup.startup_name || ficheData.startup.name;
                  // Le coup de cœur ne vise que la sélection, et seulement une
                  // startup que le juré ne finance pas.
                  const funded = alloc[selectedFiche] > 0;
                  const eligible = isFav || (ficheData.jury_visible && !funded && !voteLocked);
                  const why = !ficheData.jury_visible
                    ? 'Le coup de cœur se choisit parmi les startups de la sélection'
                    : funded ? `Vous investissez ${alloc[selectedFiche]} M€ sur cette startup : le coup de cœur distingue une startup que vous ne financez pas`
                      : voteLocked ? 'Le vote est clôturé' : null;
                  return (
                    <button onClick={() => handleSetFavorite(selectedFiche, name)}
                      disabled={favoriteSaving || !eligible}
                      title={why || (isFav
                        ? 'Cette startup est votre coup de cœur — cliquer pour le retirer'
                        : (favorite
                            ? `Votre coup de cœur est attribué à ${favorite.name} : le transférer ici`
                            : 'Désigner cette startup comme votre coup de cœur'))}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                        borderRadius: 8, fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                        cursor: !eligible ? 'not-allowed' : favoriteSaving ? 'wait' : 'pointer',
                        opacity: eligible ? 1 : 0.5,
                        border: `1.5px solid ${isFav ? '#E74C3C' : 'var(--border-strong)'}`,
                        background: isFav ? '#FDECEA' : 'transparent',
                        color: isFav ? '#C0392B' : 'var(--text)' }}>
                      {isFav ? '❤️ Coup de cœur' : '🤍 Coup de cœur'}
                    </button>
                  );
                })()}
                <button onClick={exportFichePDF} disabled={fichePdfExporting}
                  title="Exporter cette fiche en PDF, radar de résilience compris"
                  className="btn btn-outline" style={{ fontSize: 13, padding: '6px 14px' }}>
                  {fichePdfExporting ? 'Génération...' : 'Exporter PDF'}
                </button>
                <button onClick={() => setSelectedFiche(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
              </div>
            </div>

            <div className="resp-2col" style={{ marginTop: 20 }}>
              <div>
                <h4 style={{ marginBottom: 8, color: 'var(--text-light)' }}>Score ISI</h4>
                <div style={{ textAlign: 'center' }}>
                  <ScoreGauge score={ficheData.startup.note_sectorielle} size="lg" />
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    Note Pivot: <strong>{parseFloat(ficheData.startup.note_pivot || 0).toFixed(2)}</strong>
                  </div>
                </div>
                <div style={{ marginTop: 16 }}>
                  {ficheData.startup.top_force && <div style={{ fontSize: 13, color: '#27AE60' }}>Point fort: {ficheData.startup.top_force}</div>}
                  {ficheData.startup.top_faiblesse && <div style={{ fontSize: 13, color: '#E74C3C', marginTop: 4 }}>Point d'attention: {ficheData.startup.top_faiblesse}</div>}
                </div>
                {/* Info table */}
                <table style={{ width: '100%', fontSize: 13, marginTop: 16 }}>
                  <tbody>
                    {[
                      ['Secteur', ficheData.startup.categorie],
                      ['Sous-domaine', ficheData.startup.sous_domaine],
                      ['Stade', ficheData.startup.stade_dev],
                      ['CA', ficheData.startup.ca],
                      ['Effectif', ficheData.startup.effectif],
                      ['Clientèle', ficheData.startup.adressage_client],
                      ['Maturité', ficheData.startup.maturite],
                      ['Année de création', ficheData.startup.annee_creation],
                      ['Région', ficheData.startup.region],
                    ].filter(([_, v]) => v).map(([label, val]) => (
                      <tr key={label} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', color: 'var(--text-light)', width: 130 }}>{label}</td>
                        <td style={{ padding: '6px 8px', fontWeight: 500 }}>{val}</td>
                      </tr>
                    ))}
                    {ficheData.startup.site_web && (
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', color: 'var(--text-light)' }}>Site web</td>
                        <td style={{ padding: '6px 8px' }}><a href={ficheData.startup.site_web} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>{ficheData.startup.site_web}</a></td>
                      </tr>
                    )}
                    {ficheData.startup.adresse && (
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', color: 'var(--text-light)' }}>Adresse</td>
                        <td style={{ padding: '6px 8px', fontWeight: 500 }}>{ficheData.startup.adresse}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Le radar donne la forme, le tableau donne la valeur exacte :
                  les deux sont cote a cote pour etre lus ensemble, chacun sous
                  son propre titre aligne sur la meme ligne. minWidth 0 sur la
                  colonne du radar, sinon le canvas refuse de se reduire et
                  pousse le tableau hors du cadre. */}
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div ref={radarRef} style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <h4 style={{ marginBottom: 8, color: 'var(--text-light)' }}>Radar de Résilience</h4>
                  <RadarChart labels={CL_LABELS} startupData={CL_KEYS.map(k => parseFloat(ficheData.startup[k]) || 0)}
                    sectorAvg={ficheData.sector_average ? CL_KEYS.map(k => parseFloat(ficheData.sector_average[k]) || 0) : CL_KEYS.map(() => 0)}
                    startupName={ficheData.startup.startup_name || ficheData.startup.name} height={300} />
                </div>
                <div className="fiche-cluster-col">
                  <h4 style={{ marginBottom: 8, color: 'var(--text-light)' }}>Note par cluster (sur 10)</h4>
                  <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
                    <tbody>
                      {CL_KEYS.map((k, i) => {
                        const raw = ficheData.startup[k];
                        const v = raw === null || raw === undefined ? null : parseFloat(raw);
                        return (
                          <tr key={k} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '5px 6px', color: 'var(--text-light)' }}>
                              {CL_FULL_LABELS[i]}
                            </td>
                            <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 700,
                              width: 44, color: v === null ? 'var(--text-light)' : scoreColor(v) }}>
                              {v === null ? '—' : v.toFixed(1)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Présentation */}
            {ficheData.startup.pitch && (
              <div style={{ padding: 16, background: 'var(--bg-secondary)', borderRadius: 8, marginTop: 16 }}>
                <h4 style={{ marginBottom: 8 }}>Résumé exécutif</h4>
                <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-light)' }}>{ficheData.startup.pitch}</p>
              </div>
            )}

            {/* Data Room — consultation des pièces obligatoires */}
            <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border)', borderRadius: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                <h4 style={{ margin: 0, color: 'var(--text-light)' }}>Documents du dossier</h4>
                {ficheDocs && (
                  <span style={{ fontSize: 12, color: 'var(--text-light)' }}>
                    <strong style={{ color: ficheDocs.available === ficheDocs.total ? '#27AE60' : '#E67E22' }}>
                      {ficheDocs.available}
                    </strong> / {ficheDocs.total} pièces disponibles
                  </span>
                )}
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-light)', margin: '0 0 12px', lineHeight: 1.5 }}>
                Consultation uniquement, limitée aux pièces ouvertes au jury. Le reste de la
                Data Room (pièces juridiques et comptables) reste réservé à l'analyste.
                Ces documents sont confidentiels ; chaque ouverture est tracée.
              </p>

              {docError && (
                <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 6, fontSize: 12.5,
                  background: '#FFEBEE', color: '#C62828', border: '1px solid #FFCDD2' }}>
                  {docError}
                </div>
              )}

              {!ficheDocs ? (
                <div style={{ fontSize: 13, color: 'var(--text-light)' }}>Chargement des documents...</div>
              ) : (
                <div style={{ display: 'grid', gap: 6 }}>
                  {ficheDocs.documents.map(d => (
                    <div key={d.document_type} style={{ display: 'flex', alignItems: 'center', gap: 12,
                      flexWrap: 'wrap', padding: '8px 12px', borderRadius: 8, fontSize: 13,
                      background: d.available ? 'var(--bg-secondary)' : 'transparent',
                      border: `1px solid ${d.available ? 'var(--border)' : 'transparent'}`,
                      opacity: d.available ? 1 : 0.6 }}>
                      <span className="doc-row-name">{d.document_type}</span>
                      {d.available ? (
                        <>
                          <span style={{ fontSize: 11, color: 'var(--text-light)', maxWidth: 220,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={d.file_name}>
                            {d.file_name}
                          </span>
                          {d.status === 'valide' && (
                            <span className="badge badge-vert" style={{ fontSize: 10 }}>validé</span>
                          )}
                          <button onClick={() => openDocument(d)}
                            style={{ padding: '5px 14px', border: '1px solid var(--border)', borderRadius: 6,
                              background: 'white', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
                              color: 'var(--primary)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            Consulter
                          </button>
                        </>
                      ) : (
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10,
                          background: '#FFF3E0', color: '#B26A00', border: '1px solid #FFCC80' }}>
                          non fourni
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Informations complémentaires */}
            {(ficheData.startup.fondateurs || ficheData.startup.investisseurs_details || ficheData.startup.levees_details || ficheData.startup.immatriculation || ficheData.startup.linkedin) && (
              <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border)', borderRadius: 8 }}>
                <h4 style={{ marginBottom: 12, color: 'var(--text-light)' }}>Informations complémentaires</h4>
                <div className="resp-2col" style={{ gap: 12, fontSize: 13 }}>
                  {ficheData.startup.fondateurs && (
                    <div><span style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Fondateur(s)</span><div style={{ marginTop: 2 }}>{ficheData.startup.fondateurs}</div></div>
                  )}
                  {ficheData.startup.investisseurs_details && (
                    <div><span style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Investisseur(s)</span><div style={{ marginTop: 2 }}>{ficheData.startup.investisseurs_details}</div></div>
                  )}
                  {ficheData.startup.levees_details && (
                    <div><span style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Levée(s) de fonds</span><div style={{ marginTop: 2 }}>{ficheData.startup.levees_details}</div></div>
                  )}
                  {ficheData.startup.immatriculation && (
                    <div><span style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Immatriculation</span><div style={{ marginTop: 2 }}>{ficheData.startup.immatriculation}</div></div>
                  )}
                  {ficheData.startup.linkedin && (
                    <div><span style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>LinkedIn</span><div style={{ marginTop: 2 }}><a href={ficheData.startup.linkedin} target="_blank" rel="noopener noreferrer">Voir le profil</a></div></div>
                  )}
                  {ficheData.startup.contact_email && (
                    <div><span style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Email</span><div style={{ marginTop: 2 }}>{ficheData.startup.contact_email}</div></div>
                  )}
                </div>
              </div>
            )}

            {/* Mon appréciation (private comment) */}
            <div style={{ marginTop: 20, padding: 16, border: '2px solid #9C27B0', borderRadius: 10, background: '#FAF5FF' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h4 style={{ margin: 0, color: '#4A71E5' }}>Mon appréciation</h4>
                <span style={{ fontSize: 11, color: '#9C27B0', fontStyle: 'italic' }}>Visible uniquement par vous</span>
              </div>
              <textarea
                value={commentDraft}
                onChange={e => { setCommentDraft(e.target.value); setCommentSaved(false); }}
                placeholder="Saisissez votre appréciation personnelle sur cette startup..."
                rows={4}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #CE93D8',
                  fontFamily: 'inherit', fontSize: 13, resize: 'vertical', background: 'white', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                <button onClick={() => handleSaveComment(selectedFiche)} disabled={commentSaving}
                  style={{ padding: '6px 18px', borderRadius: 8, border: 'none', background: '#9C27B0',
                    color: 'white', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
                    opacity: commentSaving ? 0.6 : 1 }}>
                  {commentSaving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
                {commentDraft.trim() && (
                  <button onClick={() => handleDeleteComment(selectedFiche)} disabled={commentSaving}
                    style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #E74C3C', background: 'white',
                      color: '#E74C3C', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 500 }}>
                    Supprimer
                  </button>
                )}
                {commentSaved && (
                  <span style={{ fontSize: 12, color: '#27AE60', fontWeight: 500 }}>Enregistré</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ====== PORTEFEUILLE D'INVESTISSEMENT (onglet Sélection) ====== */}
        {tab === 'selection' && !selectedFiche && portfolio && (
          <div className="card" style={{ marginBottom: 16,
            borderLeft: `4px solid ${allocComplete ? '#27AE60' : 'var(--accent)'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0 }}>Votre portefeuille d'investissement</h3>
                <p style={{ fontSize: 13, color: 'var(--text-light)', margin: '4px 0 0', maxWidth: 620 }}>
                  Vous disposez de <strong>{RULES.budget} M€ fictifs</strong> à répartir par tranches
                  d'un million entier, sur {RULES.min_lines} à {RULES.max_lines} startups,
                  avec {RULES.max_ticket} M€ au maximum sur une même. Le montant que vous engagez
                  dit votre conviction.
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 30, fontWeight: 800,
                  color: allocRemaining === 0 ? '#27AE60' : 'var(--primary)' }}>
                  {allocRemaining} M€
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-light)' }}>
                  {allocRemaining === 0 ? 'tout est investi' : 'encore à placer'}
                </div>
              </div>
            </div>

            <BudgetChips total={allocTotal} budget={RULES.budget} />

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '14px 0 0',
              fontSize: 12.5 }}>
              {allocChecks.map((c, i) => (
                <span key={i} style={{ color: c.ok ? '#1E8449' : 'var(--text-light)', fontWeight: c.ok ? 600 : 400 }}>
                  {c.ok ? '✓' : '○'} {c.txt}
                </span>
              ))}
            </div>

            {voteLocked ? (
              <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, fontSize: 13,
                background: '#FFF8E1', border: '1px solid #FFE082', color: '#8D6E20' }}>
                Le vote a été clôturé par l'organisateur
                {portfolio.locked_at && <> le {new Date(portfolio.locked_at).toLocaleString('fr-FR')}</>}.
                Votre portefeuille est définitif.
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 16 }}>
                <button onClick={handleSavePortfolio} disabled={allocSaving || !allocDirty}
                  className="btn btn-primary" style={{ fontSize: 13.5 }}>
                  {allocSaving ? 'Enregistrement...' : allocDirty ? 'Enregistrer mon portefeuille' : 'Enregistré'}
                </button>
                <button onClick={handleResetPortfolio} disabled={allocSaving || allocTotal === 0}
                  className="btn btn-outline" style={{ fontSize: 13 }}>
                  Tout remettre à zéro
                </button>
                {portfolio.submitted_at && !allocDirty && (
                  <span style={{ fontSize: 12.5, color: '#1E8449', fontWeight: 600 }}>
                    ● Portefeuille complet, validé le {new Date(portfolio.submitted_at).toLocaleDateString('fr-FR')}
                  </span>
                )}
                {allocDirty && (
                  <span style={{ fontSize: 12.5, color: '#8D6E20' }}>
                    Modifications non enregistrées
                  </span>
                )}
              </div>
            )}

            {/* ----- Coup de cœur, hors portefeuille ----- */}
            {COUP_DE_COEUR && (
            <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px dashed var(--border)' }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 4 }}>
                ❤️ Quelle startup souhaitez-vous distinguer indépendamment de votre décision
                d'investissement ?
              </div>
              <p style={{ fontSize: 12.5, color: 'var(--text-light)', margin: '0 0 10px' }}>
                Une seule startup, à choisir parmi celles de la sélection que vous <strong>ne
                financez pas</strong>.
              </p>
              {favorite && !favorite.valid && (
                <div style={{ padding: '8px 12px', marginBottom: 10, borderRadius: 8, fontSize: 12.5,
                  background: '#FFEBEE', border: '1px solid #FFCDD2', color: '#C62828' }}>
                  {favorite.name} : {favorite.problem}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {selected.filter(p => !(alloc[p.id] > 0)).map(p => {
                  const isFav = favorite && favorite.startup_id === p.id;
                  return (
                    <button key={p.id} type="button" disabled={voteLocked || favoriteSaving}
                      onClick={() => handleSetFavorite(p.id, p.name)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '7px 13px', borderRadius: 999, fontFamily: 'inherit', fontSize: 13,
                        fontWeight: isFav ? 700 : 500, cursor: voteLocked ? 'not-allowed' : 'pointer',
                        border: `1.5px solid ${isFav ? '#E74C3C' : 'var(--border-strong)'}`,
                        background: isFav ? '#FDECEA' : 'transparent',
                        color: isFav ? '#C0392B' : 'var(--text)' }}>
                      {isFav ? '❤️' : '🤍'} {p.name}
                    </button>
                  );
                })}
                {selected.filter(p => !(alloc[p.id] > 0)).length === 0 && (
                  <span style={{ fontSize: 12.5, color: 'var(--text-light)' }}>
                    Toutes les startups de la sélection sont financées : libérez-en une pour pouvoir
                    désigner un coup de cœur.
                  </span>
                )}
              </div>
            </div>
            )}
          </div>
        )}

        {/* ====== LISTE DES PARTICIPANTS / DE LA SÉLECTION ======
            Même affichage et mêmes filtres : seule la liste de départ change. */}
        {(tab === 'participants' || tab === 'selection') && !selectedFiche && (
          <div style={{ display: 'grid', gap: 8 }}>
            {filtered.map((p, idx) => (
              <div key={p.id} className="card" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}
                onClick={() => openFiche(p.id)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <span style={{ width: 24, fontWeight: 700, fontSize: 14, textAlign: 'center', color: 'var(--text-light)' }}>
                    {idx + 1}
                  </span>
                  <LogoImg src={p.logo_url} name={p.name} size={32} />
                  <div>
                    <strong>{p.name}</strong>
                    {favorite && favorite.startup_id === p.id && (
                      <span title="Votre coup de cœur" style={{ marginLeft: 6 }}>❤️</span>
                    )}
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                      <span className="badge badge-secteur" style={{ fontSize: 10 }}>{p.categorie}</span>
                      {p.sous_domaine && <span className="badge" style={{ fontSize: 10 }}>{p.sous_domaine}</span>}
                      {/* Affiché dans l'onglet Participants seulement : dans
                          l'onglet Sélection, toutes les lignes sont sélectionnées. */}
                      {tab === 'participants' && <SelectionBadge selected={p.jury_visible} />}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                  {/* Curseur d'investissement : présent uniquement dans l'onglet
                      Sélection, et neutralisé une fois le vote clôturé. Les clics
                      ne doivent pas ouvrir la fiche, d'où stopPropagation. */}
                  {tab === 'selection' && portfolio && (
                    <div onClick={e => e.stopPropagation()}
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button type="button" disabled={voteLocked || !(alloc[p.id] > 0)}
                        onClick={() => setAmount(p.id, (alloc[p.id] || 0) - 1)}
                        style={allocBtnStyle(voteLocked || !(alloc[p.id] > 0))}>−</button>
                      <div style={{ minWidth: 74, textAlign: 'center' }}>
                        <div style={{ fontSize: 17, fontWeight: 800,
                          color: alloc[p.id] > 0 ? '#C8880A' : 'var(--text-light)' }}>
                          {alloc[p.id] > 0 ? `${alloc[p.id]} M€` : '—'}
                        </div>
                        {alloc[p.id] > 0 && (
                          <div style={{ fontSize: 9.5, fontWeight: 600,
                            color: convictionLabel(alloc[p.id]).color }}>
                            {convictionLabel(alloc[p.id]).txt}
                          </div>
                        )}
                      </div>
                      <button type="button"
                        disabled={voteLocked || allocRemaining === 0 || (alloc[p.id] || 0) >= RULES.max_ticket}
                        onClick={() => setAmount(p.id, (alloc[p.id] || 0) + 1)}
                        style={allocBtnStyle(voteLocked || allocRemaining === 0 || (alloc[p.id] || 0) >= RULES.max_ticket)}>+</button>
                    </div>
                  )}
                  {tab === 'selection' && favorite && favorite.startup_id === p.id && (
                    <span title="Votre coup de cœur" style={{ fontSize: 16 }}>❤️</span>
                  )}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: parseFloat(p.note_sectorielle) > 7 ? '#27AE60' : '#F39C12' }}>
                      {p.note_sectorielle ? parseFloat(p.note_sectorielle).toFixed(2) : '-'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-light)' }}>Score ISI</div>
                  </div>
                  {p.risk_level && <RiskBadge level={p.risk_level} />}
                  {comments[p.id] && (
                    <span title="Vous avez une appréciation sur cette startup" style={{ fontSize: 16, cursor: 'default' }}>📝</span>
                  )}
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-light)' }}>
                {/* Distinguer "les filtres ne renvoient rien" de "l'administrateur
                    n'a encore soumis aucune startup au jury" : sans cela, un jure
                    croit a un probleme de filtres alors que la selection est vide. */}
                {baseList.length === 0
                  ? (tab === 'selection'
                      ? "L'organisateur n'a encore retenu aucune startup dans la sélection. "
                        + "Les candidatures restent consultables dans l'onglet Participants."
                      : "Aucune candidature n'a encore été soumise pour cette campagne.")
                  : 'Aucune startup ne correspond aux filtres sélectionnés'}
              </div>
            )}
          </div>
        )}

        {/* ====== RÉSULTAT DU VOTE ======
            Même bloc que celui de l'organisateur, sans ce qui lui est réservé :
            ni export, ni clôture, ni détail nominatif par juré. */}
        {tab === 'results' && !selectedFiche && (
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginBottom: 8 }}>Résultat du vote du jury</h3>
            <JuryResultsBoard results={voteResults} error={voteResultsError} />
          </div>
        )}

        {/* ====== RANKING ====== */}
        {tab === 'ranking' && ranking && (
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginBottom: 16 }}>Classement ISI — {activeCampaign.name}</h3>
            <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 16 }}>
              {ranking.total} startups classées · Note moyenne: <strong>{ranking.average.toFixed(2)}/10</strong>
            </p>
            <p style={{ fontSize: 12.5, color: '#8D6E20', background: '#FFF8E1', border: '1px solid #FFE082',
              borderRadius: 8, padding: '8px 12px', marginBottom: 16 }}>
              Le classement porte sur la <strong>sélection retenue par l'organisateur</strong>.
              Les autres candidatures soumises restent consultables dans l'onglet Participants,
              sans figurer ici.
            </p>
            {ranking.ranking.map((r, idx) => {
              // Une candidature soumise mais pas encore notée n'a pas de note :
              // afficher « — » et une jauge vide, plutôt que « NaN » et une
              // barre pleine, qui se lisait comme un score maximal.
              const note = r.note_sectorielle != null ? parseFloat(r.note_sectorielle) : null;
              const pct = note != null ? (note / 10) * 100 : 0;
              const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '';
              return (
                <div key={r.id} className="rank-row" style={{ marginBottom: 8, cursor: 'pointer' }}
                  onClick={() => openFiche(r.id)}>
                  <span style={{ width: 32, textAlign: 'center', fontWeight: 700, fontSize: 14 }}>{medal || `#${r.rank}`}</span>
                  <span className="rank-name" style={{ fontSize: 14, fontWeight: 500 }}>{r.name}</span>
                  <div className="rank-bar" style={{ height: 24, background: '#F0F0F0', borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: 12,
                      background: pct > 70 ? 'linear-gradient(90deg, #27AE60, #2ECC71)' : pct > 40 ? 'linear-gradient(90deg, #F39C12, #F1C40F)' : 'linear-gradient(90deg, #E74C3C, #E67E22)' }} />
                  </div>
                  <span style={{ width: 60, textAlign: 'right', fontWeight: 700, fontSize: 15,
                    color: note == null ? 'var(--text-light)' : note > 7 ? '#27AE60' : '#F39C12' }}>
                    {note == null ? '—' : note.toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* ====== MAP ====== */}
        {tab === 'map' && (() => {
          const withAddress = filtered.filter(p => p.adresse && p.latitude && p.longitude);
          return (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Carte des startups sélectionnées</div>
              <div style={{ fontSize: 13, color: 'var(--text-light)' }}>
                <strong style={{ color: 'var(--accent)' }}>{withAddress.length}</strong> affichées sur la carte
                {' '} / {selected.length} startups sélectionnées
                {withAddress.length < filtered.length && (
                  <span style={{ fontSize: 11 }}> (seules les startups avec adresse postale sont affichées)</span>
                )}
              </div>
            </div>
            <div style={{ height: 'min(620px, 70vh)' }}>
              <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css" />
              <MapContainer
                center={[46.8, 2.5]}
                zoom={6}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
                zoomControl={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapBoundsUpdater participants={withAddress} />
                {withAddress.map(p => (
                    <Marker key={p.id} position={[parseFloat(p.latitude), parseFloat(p.longitude)]}
                      icon={riskIcon(p.risk_level)}
                      eventHandlers={{ click: () => openFiche(p.id) }}>
                      <Popup>
                        <div style={{ minWidth: 200 }}>
                          <strong style={{ fontSize: 14 }}>{p.name}</strong>
                          <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{p.categorie} — {p.sous_domaine}</div>
                          <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ fontWeight: 700, fontSize: 16,
                              color: parseFloat(p.note_sectorielle) > 7 ? '#27AE60' : parseFloat(p.note_sectorielle) >= 4 ? '#F39C12' : '#E74C3C' }}>
                              {parseFloat(p.note_sectorielle).toFixed(2)}
                            </span>
                            <span style={{ fontSize: 11, color: '#999' }}>/ 10</span>
                          </div>
                          <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>{p.adresse}</div>
                          <button onClick={() => openFiche(p.id)}
                            style={{ marginTop: 8, padding: '4px 12px', background: 'var(--primary)', color: 'white',
                              border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}>
                            Voir la fiche
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                ))}
              </MapContainer>
            </div>
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 16, alignItems: 'center', fontSize: 12, color: 'var(--text-light)' }}>
              <span>{withAddress.length} startups localisées sur {selected.length} sélectionnées</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#27AE60', display: 'inline-block' }} /> Vert (&gt;7)
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#F39C12', display: 'inline-block' }} /> Orange (4-7)
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#E74C3C', display: 'inline-block' }} /> Rouge (&lt;4)
              </span>
            </div>
          </div>
          );
        })()}

        {/* ====== MES APPRECIATIONS ====== */}
        {tab === 'notes' && (
          <div>
            {/* Commentaire général */}
            <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid #9C27B0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, color: '#4A71E5' }}>Mon appréciation générale</h3>
                <span style={{ fontSize: 11, color: '#9C27B0', fontStyle: 'italic' }}>Visible uniquement par vous</span>
              </div>
              <textarea
                value={generalComment}
                onChange={e => { setGeneralComment(e.target.value); setGeneralSaved(false); }}
                placeholder="Saisissez votre appréciation générale sur cette campagne de Trophées..."
                rows={5}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #CE93D8',
                  fontFamily: 'inherit', fontSize: 13, resize: 'vertical', background: '#FAF5FF', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                <button onClick={handleSaveGeneral} disabled={generalSaving}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#9C27B0',
                    color: 'white', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                    opacity: generalSaving ? 0.6 : 1 }}>
                  {generalSaving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
                {generalSaved && <span style={{ fontSize: 12, color: '#27AE60', fontWeight: 500 }}>Enregistré</span>}
              </div>
            </div>

            {/* Liste des appréciations par startup */}
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Mes appréciations par startup</h3>
                {favorite && (
                  <span style={{ fontSize: 12.5, color: 'var(--text-light)' }}>
                    Coup de cœur : <strong style={{ color: '#C0392B' }}>❤️ {favorite.name}</strong>
                    {!favorite.jury_visible && (
                      <span style={{ color: '#8D6E20' }}> — hors sélection</span>
                    )}
                  </span>
                )}
              </div>
              {(() => {
                const commented = participants.filter(p => comments[p.id]);
                if (commented.length === 0) return (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>📝</div>
                    <p>Vous n'avez pas encore saisi d'appréciation.</p>
                    <p style={{ fontSize: 13 }}>Ouvrez la fiche d'une startup dans l'onglet "Participants" pour saisir votre première appréciation.</p>
                  </div>
                );
                return (
                  <div style={{ display: 'grid', gap: 12 }}>
                    {commented.map(p => (
                      <div key={p.id} style={{ padding: '14px 16px', border: '1px solid #E1BEE7', borderRadius: 10, background: '#FAFAFA' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          {/* Le statut de sélection vient de `jury_visible`, relu à
                              chaque chargement : une startup commentée peut entrer
                              ou sortir de la sélection entre deux visites. */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <LogoImg src={p.logo_url} name={p.name} size={28} />
                            <strong style={{ fontSize: 14 }}>{p.name}</strong>
                            {favorite && favorite.startup_id === p.id && (
                              <span title="Votre coup de cœur" style={{ fontSize: 13 }}>❤️</span>
                            )}
                            <SelectionBadge selected={p.jury_visible} />
                            <span className="badge badge-secteur" style={{ fontSize: 9 }}>{p.categorie}</span>
                            <span style={{ fontSize: 14, fontWeight: 700,
                              color: p.note_sectorielle == null ? 'var(--text-light)'
                                : parseFloat(p.note_sectorielle) > 7 ? '#27AE60' : '#F39C12' }}>
                              {p.note_sectorielle == null ? '—' : parseFloat(p.note_sectorielle).toFixed(2)}
                            </span>
                          </div>
                          <button onClick={() => openFiche(p.id)}
                            style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid var(--primary)',
                              background: '#F5F9FF', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit',
                              color: 'var(--primary)', fontWeight: 600 }}>
                            Ouvrir la fiche
                          </button>
                        </div>
                        <div style={{ padding: '8px 12px', background: '#FAF5FF', borderRadius: 8, border: '1px solid #E1BEE7',
                          fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', color: '#333' }}>
                          {comments[p.id]}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
